import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-serif">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link 
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img 
              src="https://public.readdy.ai/ai/img_res/9f4ee01b-9e0a-4c3a-81f6-f6867df1cc30.png" 
              alt="Tory Stephens" 
              className="h-12 w-12 rounded-full object-cover cursor-pointer"
            />
          </Link>
          <div className="flex items-center gap-8">
            <Link 
              to="/about" 
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              About
            </Link>
            <a 
              href="#selected-work" 
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Selected Work
            </a>
            <a 
              href="https://bsky.app" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Bluesky
            </a>
            <a 
              href="https://linkedin.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              LinkedIn
            </a>
            <a 
              href="#contact" 
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get in touch
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-44 pb-40 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-6xl md:text-7xl font-light leading-tight text-gray-900 mb-10">
            Tory Stephens
          </h1>
          <p className="text-2xl md:text-3xl font-light leading-relaxed text-gray-700 max-w-2xl mb-16">
            Storyteller, strategist, and climate justice advocate working at the intersection of culture, justice, and climate futures.
          </p>
          <div className="w-full h-[420px] overflow-hidden">
            <img 
              src="https://static.readdy.ai/image/5ceff2538926af40634d78d14c5cc996/b29089f06c9b43a28ea5549edba6cc4e.png" 
              alt="Tory Stephens" 
              className="w-full h-full object-cover object-[center_15%]"
            />
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section id="about" className="py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xl leading-relaxed text-gray-800 max-w-2xl">
            I work at the intersection of climate, culture, and justice—helping stories travel beyond policy circles and into the imaginations of everyday people.
          </p>
        </div>
      </section>

      {/* Work at Grist */}
      <section id="selected-work" className="py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-8">
            Selected Work
          </h2>
          <p className="text-lg leading-relaxed text-gray-800 max-w-2xl">
            At Grist, I co-founded Imagine 2200, a climate fiction initiative for future ancestors. I've helped produce two anthologies—<em>Afterglow</em> and <em>Metamorphosis</em>—and now focus on publishing climate fiction year-round while building partnerships that connect narrative, movement work, and real-world climate solutions.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-8">
            Why Culture
          </h2>
          <p className="text-lg leading-relaxed text-gray-800 max-w-2xl">
            I believe <strong className="font-medium text-gray-900">culture is infrastructure</strong>. The stories we tell—through fiction, art, and media—shape what futures feel possible, who belongs in them, and how we act in the present.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-40 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-10">
            Contact
          </h2>
          <p className="text-lg leading-relaxed text-gray-800 mb-10 max-w-2xl">
            For speaking, collaborations, or conversations about climate storytelling:
          </p>
          <a 
            href="mailto:toryjstephens@gmail.com" 
            className="inline-block text-xl text-gray-900 hover:text-gray-600 transition-colors border-b-2 border-gray-900 hover:border-gray-600 pb-1 cursor-pointer whitespace-nowrap"
          >
            toryjstephens@gmail.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-200">
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} Tory Stephens. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Website Builder
          </a>
        </div>
      </footer>
    </div>
  );
}
